import React, { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { playClickSound } from '@/utils/soundUtils';
import { modalWidthStyle, MODAL_Z } from '@/styles/modalTokens';
import { typeRole } from '@/styles/typography';
import { useModalMotion } from '@/hooks/useModalMotion';

export interface IosPwaGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function IosPwaGuideModal({
  isOpen,
  onClose,
}: IosPwaGuideModalProps) {
  const { t } = useTranslation();
  const { render, backdropClass, panelClass } = useModalMotion(isOpen);
  const titleId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const option3HtmlUrl = `${import.meta.env.BASE_URL}ios_guide/option-3-dark-premium.html`;

  useEffect(() => {
    if (!isOpen) return;
    closeButtonRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [isOpen, onClose]);

  if (!render) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-4 md:p-6" style={{ zIndex: MODAL_Z.pwa }} role="presentation">
      <button
        type="button"
        className={`absolute inset-0 bg-black/75 backdrop-blur-sm ${backdropClass}`}
        aria-hidden
        tabIndex={-1}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`relative w-full rounded-[24px] border border-border-ice cosmos-modal-panel ${panelClass} p-3 md:p-4 shadow-[0_28px_70px_rgba(0,0,0,0.58)]`}
        style={modalWidthStyle('--wf-mdl-guide-w')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between gap-3 px-1">
          <h2 id={titleId} className={`${typeRole.header2} font-semibold text-ink leading-tight`}>
            {t('supportRail.iosGuide.title')}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border-ice text-ice transition hover:bg-white/10 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--text-high-light,#22d3ee)]"
            aria-label={t('supportRail.closeIosGuideAriaLabel')}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <iframe
          src={option3HtmlUrl}
          title={t('supportRail.iosGuide.title')}
          className="block h-[min(84vh,860px)] w-full rounded-[18px] border border-white/12 bg-[#0b1220]"
        />
      </div>
    </div>
  );
}
